import { Identifiable, RecordModel } from "../core";
import { UnitModel, IUnit } from "./housing";
import RecordSelect from "../../components/fields/RecordSelect";
import { Tag } from "antd";
import { CaretRightOutlined, SketchOutlined, SyncOutlined } from '@ant-design/icons';
import YearSelect from "../../components/fields/YearSelect";
import React from "react";
import { OwnerType } from "../coreModels";
import BookingPreview from "../../components/business/BookingPreview";
import moment from "moment/moment";


export interface IBooking extends Identifiable {
  unit: IUnit;
  unit_owner?: OwnerType;
  start_date: string;
  end_date: string;
  is_preferred: boolean;
  for_rent: boolean;
  round: number;
  data?: {
    assignment: {
      rank: number; week: number; round: number; fraction: number; preferred: boolean;
    }
  }
}

export const BookingModel: RecordModel<IBooking> = {
  name: "Booking",
  path: "bookings",
  selectQuery: "",
  tableQuery: "&expand=unit,unit_owner.owner",
  toDescriptor: (r, extraProps) => <BookingPreview {...extraProps} booking={r} />,
  toString: r => r.start_date,
  contextActions: [
    { name: 'Add comment', path: 'comments' },
  ],
  customCreateFields: [
    { name: 'round', component: null },
    { name: 'data', component: null },
  ],
  fieldModels: {
    unit_owner: 'Owner',
    round: 'BookingRound',
  },
  tableFilters: [
    { selectModel: UnitModel },
    { component: () => <YearSelect filter /> }
  ],
  showsChangeLog: true,
}


export interface ICalendarNote extends Identifiable {
  unit: IUnit;
  unit_owner?: OwnerType;
  start_date: string;
  end_date: string;
  title: string;
  body: string | null;
}

export const CalendarNoteModel: RecordModel<ICalendarNote> = {
  name: "Calendar Note",
  path: "calendar-notes",
  selectQuery: "",
  tableQuery: "",
  toString: r => r.title,
  fieldModels: {
    unit_owner: 'Owner'
  }
}


export interface IBookingRound extends Identifiable {
  year: number;
  unit: number | IUnit;
  weekday_start: number;
  start_index: number;
  start_date: string;
  end_date: string;
  active: boolean;
  weeks_required: number;
  usage_date_start: string;
  usage_date_end: string;
  closed: boolean;
  owners: any[];
}

export const BookingRoundModel: RecordModel<IBookingRound> = {
  name: "Booking Round",
  path: "booking-rounds",
  selectQuery: "&expand=unit.name&fields=id,unit.name,year",
  tableQuery: "&expand=unit&fields=*,unit.name,unit.id",
  toString(r: IBookingRound): string {
    const unitStr = typeof r.unit === 'number' ? r.unit.toString() : r.unit.name;
    return `${unitStr} - ${r.year}`;
  },
  detailAction: true,
  customTableMappings: {
    ...Object.fromEntries(['usage_date_start', 'usage_date_end', 'owners', 'closed', 'owners_status'].map(fieldName => ([fieldName, { omit: true }]))),
    unit: { path: ['unit', 'name'], width: 200 },
    active: { width: 100 },
    weekday_start: { title: 'Check-in day', render: br => moment().day((br.weekday_start || 0) % 7).format('dddd') },
    start_index: { title: 'First fraction', render: br => <Tag>#{br.start_index}</Tag> },
  },
  tableFilters: [
    { selectModel: UnitModel },
    { component: () => <YearSelect filter /> },
  ],
  /*
  crossTableQuery: '&expand=unit&fields=*,unit.name,unit.id',
  crossRecordViews: [
    { toModel: OwnerModel, toExtraQuery: '&expand=unit,owner&fields=id,owner,unit', invertedRel: true, relName: 'owners' }
  ],
  */
  customActions: [
    {
      name: "Run algorithm",
      isDetail: true,
      path: "persist-assignments",
      icon: <CaretRightOutlined />,
      hidden: () => true
    }
  ]
}

export interface ISpecialStay extends Identifiable {
  units: (number | IUnit)[];
  name: string;
  checkin: string;
  days: number;
  type: string;
}

export const SpecialStayModel: RecordModel<ISpecialStay> = {
  name: "Special Stay",
  path: "special-stays",
  selectQuery: "",
  tableQuery: "&expand=units&fields=*,units.name,units.id",
  toString(r: ISpecialStay): string {
    return `${r.name} - ${r.type}`;
  },
  icon: <SketchOutlined />,
  customCreateFields: [
    { name: "units", component: (props) => <RecordSelect {...props} recordModel={UnitModel} mode="multiple" formName="units" /> },
  ],
  customTableMappings: {
    units: {
      render: (
        r => (
          <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 4 }}>
            {r.units.length === 0 && <Tag color="blue">All units</Tag>}
            {r.units.map(u => <Tag style={{ letterSpacing: '-0.5px' }}>{typeof u === 'number' ? u.toString() : u.name}</Tag>)}
          </div>
        )
      )
    },
  },
  tableFilters: [
    { selectModel: UnitModel },
    { selectField: 'type' },
    { component: (q, props) => <YearSelect filter {...props} /> },
  ],
  crossTableQuery: '&fields=id,name,units',
  crossRecordViews: [
    { toModel: UnitModel, toExtraQuery: '&fields=id,name', invertedRel: true, relName: 'units' }
  ],
}

export interface ILodgifyProperty extends Identifiable {
  lodgify_id: number;
  name: string;
  subscription_plans: string[];
  latitude: string;
  longitud: string;
  created_at: string;
  modified_at: string;
}

export const LodgifyPropertyModel: RecordModel<ILodgifyProperty> = {
  name: "Lodgify Property",
  namePlural: "Lodgify Properties",
  path: "lodgify-properties",
  selectQuery: "",
  tableQuery: "",
  toString: r => `${r.lodgify_id} - ${r.name}`,
  customTableMappings: {
    name: { width: '25%' },
    subscription_plans: { render: r => r.subscription_plans.join(', ') }
  },
  customActions: [
    { name: 'Reload from lodgify', path: 'sync-properties', icon: <SyncOutlined /> },
  ]
}

export interface ILodgifyBooking extends Identifiable {
  lodgify_id: number;
  property: ILodgifyProperty;
  guest: any;
  language: string;
  source: string;
  created_at: string;
  modified_at: string;
  total_amount: string;
  currency: string;
  arrival: string;
  departure: string;
  check_in: string;
  check_out: string;
}

export const LodgifyBookingModel: RecordModel<ILodgifyBooking> = {
  name: "Lodgify Booking",
  path: "lodgify-bookings",
  selectQuery: "",
  tableQuery: "&expand=property,guest",
  toString: r => `${r.lodgify_id} - prop ${r.property}, ${r.source}`,
  customTableMappings: {
    lodgify_id: { render: r => <small>{r.lodgify_id}</small>, width: 80 },
    property: { render: r => r.property.name },
    guest: { render: r => r.guest.name, width: 150 },
    check_in: { omit: true },
    check_out: { omit: true },
    language: { omit: true },
    arrival: { width: 105 },
    departure: { width: 105 },
    currency: { width: 60 },
    status: { width: 85 },
    source: { width: 100 },
    total_amount: { width: 110 },
  },
  allowSearch: true,
  tableFilters: [
    { filter: 'property', selectModel: LodgifyPropertyModel },
    { selectField: 'source' }
  ],
  customActions: [
    { name: 'Sync Bookings', path: 'sync-bookings', icon: <SyncOutlined /> }
  ]
}


export interface IWeekAssignmentRound extends Identifiable {
  booking_round: number | IBookingRound;
  version: number;
  status: string;
  notes: string;
  created_by: number;
  finalized_date: string;
  based_on_version: number;
  total_assignments: number;
  preferred_assignments: number;
}

export const WeekAssignmentRoundModel: RecordModel<IWeekAssignmentRound> = {
  name: "Week Assignment Round",
  path: "week-assignment-rounds",
  selectQuery: "&expand=booking_round.unit.name&fields=id,booking_round.unit.name,booking_round.year,version",
  tableQuery: "&expand=booking_round,booking_round.unit",
  toString: r => {
    const br = r.booking_round as IBookingRound;
    const unit = br.unit as IUnit;
    return `${unit.name} ${br.year} v${r.version}`;
  },
  detailAction: true,
  fieldModels: {
    created_by: 'User',
  },
  customActions: []
}


export interface IWeekAssignment extends Identifiable {
  assignment_round: number;
  unit_owner: number;
  fraction: number;
  start_date: string;
  end_date: string;
  week_number: number;
  from_preferred_list: boolean;
  preferred_rank: number;
  assignment_rank: number;
  price_average: number;
  stay_type: string;
  notes: string;
}

export const WeekAssignmentModel: RecordModel<IWeekAssignment> = {
  name: "Week Assignment",
  path: "week-assignments",
  selectQuery: "",
  tableQuery: "&expand=unit_owner,unit_owner.owner",
  toString: r => `${r.unit_owner} ${r.start_date}`,
  fieldModels:{
    assignment_round: 'WeekAssignmentRound',
    unit_owner: 'Owner'
  },
  tableFilters: [
    { selectModel: WeekAssignmentRoundModel, filter: 'assignment_round' },
    { selectField: 'round' }
  ]
}

export interface IAssignmentChange extends Identifiable {
  assignment_round: number;
  change_type: string;
  reason: string;
  made_by: number;
  timestamp: string;
  fraction_a: number;
  fraction_b: number;
  week_a: number;
  week_b: number;
  affected_fraction: number;
  affected_week: number;
  metadata: any;
}

export const AssignmentChangeModel: RecordModel<IAssignmentChange> = {
  name: "Assignment Change",
  path: "assignment-changes",
  selectQuery: "",
  tableQuery: "&expand=made_by",
  toString: r => `${r.change_type} ${r.timestamp}`,
}
